import { useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';
import { LightbulbIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { callGet } from '@/api/fetcher';
import { skipAppVersion } from '@/redux/appVersion/actions';
import { isSkippingAppVersion } from '@/redux/appVersion/selectors';

const UPDATE_TOAST_ID = 'update';
const HEALTHCHECK_POLL_INTERVAL_MS = 5 * 60 * 1000;

export const useNewVersion = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const updateLater = useSelector(isSkippingAppVersion);

  const { data: version } = useQuery({
    queryKey: ['healthcheck'],
    queryFn: () => callGet('/healthcheck'),
    refetchInterval: HEALTHCHECK_POLL_INTERVAL_MS,
  });

  useEffect(() => {
    if (version === undefined || updateLater || version.version === window.IMAGE_TAG) {
      return;
    }
    toast(t('update_notification.title'), {
      id: UPDATE_TOAST_ID,
      description: t('update_notification.subtitle'),
      icon: <LightbulbIcon size={16} className="text-primary" />,
      duration: Infinity,
      // Borrows the informational accent without `toast.info()`, which the
      // `showToasts.info` preference can hide. The prompt must always show.
      classNames: { toast: '!border-l-foreground' },
      action: { label: t('update_notification.button1'), onClick: () => navigate(0) },
      cancel: {
        label: t('update_notification.button2'),
        onClick: () => dispatch(skipAppVersion()),
      },
    });
  }, [version, updateLater, dispatch, navigate, t]);
};
